/**
 * Block 13 PR-G Phase 6.5G hotfix (2026-05-25) — SpecRowEditor compact tabular
 * layout, mirror Block 12 `SectionComposition` (Aibek visual verify round 6
 * fix #2).
 *
 * **Two layouts** в зависимости от типа строки:
 *
 *   1. **Custom row** (`row.key !== 'block_cost'`, `removable: true`):
 *      Compact thin `<li>` flex-row — 3 dashed-underline inputs (Название /
 *      Тип / Бренд) side-by-side + Trash button (44×44 tap target). Точный
 *      mirror Block 12 `CompositionRowItem`. **Cost больше не редактируется
 *      inline на custom row** — итог по блоку выражается через единственный
 *      block_cost row внизу таблицы; per-row cost не отображается ни в UI ни
 *      в PDF (drawSpecBodyRow рендерит только [label, type, brand]). Schema
 *      `row.cost?` preserved для fromQuote seed back-compat и selectTotalSpec
 *      aggregation, но UI exposure убрана для compact layout.
 *
 *   2. **block_cost row** (`row.key === 'block_cost'`, `!row.removable`):
 *      Full-width footer-style row — readonly label «Стоимость» слева + cost
 *      numeric input справа с currency suffix (₸/₽/сум). Renders отдельно
 *      под compact table в `SpecBlockEditor`. NO type/brand/remove —
 *      это итог по блоку, не позиция.
 *
 * **Empty cost behaviour:**
 *   - block_cost: empty → 0 (schema seeds cost:0).
 *
 * **Empty type/brand behaviour:** пустая строка → undefined (Schema
 * `.optional()` semantics; PDF skip undefined cells без «—» placeholder).
 *
 * **Testids:**
 *   - block_cost: `{prefix}-label-readonly`, `{prefix}-cost-input`.
 *   - Custom: `{prefix}-label-input`, `{prefix}-type-input`, `{prefix}-brand-input`,
 *     `{prefix}-remove`. **NO `{prefix}-cost-input` on custom rows** (cost UI
 *     removed per compact layout — see SpecRowEditor.test.tsx update).
 *
 * Reference:
 *   - Block 12 `apps/web/src/features/calculator/quote/sections/SectionComposition.tsx`
 *     (`CompositionRowItem` pattern source).
 *   - Aibek screenshot `Спецификация.png` (target visual reference, КП Block 12).
 */

import { Trash2 } from 'lucide-react';
import type { ContractSpecRow } from '@pomoshnik/shared';

/**
 * Dashed-underline cell input — mirror Block 12 `CELL_INPUT_CLASS`. Focus
 * state: solid border + faint accent background highlight.
 */
const CELL_INPUT_CLASS =
  'w-full min-h-tap px-1 py-1 bg-transparent border-0 border-b border-dashed border-accent/70 text-base text-ink placeholder:text-muted/60 placeholder:italic focus:border-solid focus:border-cta focus:bg-cta/10 focus:outline-none';

const COST_INPUT_CLASS =
  'w-full min-h-tap px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink font-semibold ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none placeholder:text-ink/40';

export interface SpecRowEditorProps {
  row: ContractSpecRow;
  /** № в визуальной нумерации (1-based) для aria labels. */
  index: number;
  /** Currency suffix display ('₸' / '₽' / 'сум'). */
  currencyShort: string;
  /** Test-id prefix для scoping. */
  testIdPrefix: string;
  /** Только для removable rows; для block_cost — caller передаёт no-op. */
  onUpdateLabel: (value: string) => void;
  /** Тип optional field. Empty → undefined. Только custom rows. */
  onUpdateType: (value: string | undefined) => void;
  /** Бренд optional field. Empty → undefined. Только custom rows. */
  onUpdateBrand: (value: string | undefined) => void;
  /** Только block_cost (custom row cost UI removed в Phase 6.5G hotfix). */
  onUpdateCost: (value: number | undefined) => void;
  /** Только для removable rows; для block_cost — caller передаёт no-op. */
  onRemove: () => void;
}

export function SpecRowEditor({
  row,
  index,
  currencyShort,
  testIdPrefix,
  onUpdateLabel,
  onUpdateType,
  onUpdateBrand,
  onUpdateCost,
  onRemove,
}: SpecRowEditorProps) {
  const isBlockCost = row.key === 'block_cost';

  if (isBlockCost) {
    return (
      <div
        data-testid={testIdPrefix}
        className="mt-3 rounded-xl border-[1.5px] border-accent bg-card px-3 py-3 flex items-center gap-3"
      >
        <label
          htmlFor={`${testIdPrefix}-cost`}
          data-testid={`${testIdPrefix}-label-readonly`}
          className="text-base font-bold text-ink uppercase tracking-wide"
        >
          Стоимость
        </label>
        <div className="relative flex-1">
          <input
            id={`${testIdPrefix}-cost`}
            type="number"
            inputMode="numeric"
            min={0}
            step={1000}
            value={row.cost === 0 || row.cost === undefined ? '' : row.cost}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                onUpdateCost(0);
                return;
              }
              onUpdateCost(Math.max(0, Number(raw) || 0));
            }}
            aria-label={`Стоимость блока в ${currencyShort}`}
            data-testid={`${testIdPrefix}-cost-input`}
            placeholder="0"
            className={`${COST_INPUT_CLASS} pr-12`}
          />
          <span
            aria-hidden="true"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 text-sm font-semibold pointer-events-none"
          >
            {currencyShort}
          </span>
        </div>
      </div>
    );
  }

  // Custom row — compact thin flex layout (mirror Block 12 CompositionRowItem).
  const rowName = row.label.trim() || `позиция ${index}`;

  return (
    <li
      data-testid={testIdPrefix}
      className="flex items-center gap-1.5 py-1 border-b border-canvas"
    >
      <input
        type="text"
        value={row.label}
        onChange={(e) => onUpdateLabel(e.target.value)}
        placeholder="—"
        aria-label={`Название позиции ${rowName}`}
        data-testid={`${testIdPrefix}-label-input`}
        className={`${CELL_INPUT_CLASS} flex-[2_1_0] min-w-0 font-semibold`}
      />
      <input
        type="text"
        value={row.type ?? ''}
        onChange={(e) => onUpdateType(e.target.value === '' ? undefined : e.target.value)}
        placeholder="—"
        aria-label={`Тип для позиции ${rowName}`}
        data-testid={`${testIdPrefix}-type-input`}
        className={`${CELL_INPUT_CLASS} flex-[1_1_0] min-w-0`}
      />
      <input
        type="text"
        value={row.brand ?? ''}
        onChange={(e) => onUpdateBrand(e.target.value === '' ? undefined : e.target.value)}
        placeholder="—"
        aria-label={`Бренд для позиции ${rowName}`}
        data-testid={`${testIdPrefix}-brand-input`}
        className={`${CELL_INPUT_CLASS} flex-[1_1_0] min-w-0`}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Удалить позицию ${rowName}`}
        data-testid={`${testIdPrefix}-remove`}
        className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md text-destructive focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
      >
        <Trash2 size={18} aria-hidden="true" />
      </button>
    </li>
  );
}
